import React, { useEffect, useRef, useState, FC } from 'react';
import Slider from 'react-slick';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import Breakpoints from '../../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import { TGalleryItem } from '../../../../../types/media.types';
import Icon from '../../../icon';
import HeroCard from '../../../overlayCarouselPopup/components/carouselCard';
import { TMosaicModalProps } from './types/mosaicModal.type';

import ArrowLeft from './arrowLeft';
import ArrowRight from './arrowRight';
import Picture from '../../../picture';
import { TImageField } from '../../../../../types/sitecore.types';
import './styles.scss';

const MiniTile = (index: number, item: TGalleryItem, selectedItemIndex: number | undefined): JSX.Element => {
  return (
    <Picture
      editorialImageProps={
        item.editorialImageProps && {
          ...item.editorialImageProps,
          isVideoThumbnail: item.isVideo,
          cssClassPictureEl: `modal-mosaic__mini-tile-picture ${
            selectedItemIndex === index ? 'modal-mosaic__mini-tile-picture--active' : ''
          }`,
        }
      }
      productImageProps={
        item.mosaicModalProductImageProps && {
          ...item.mosaicModalProductImageProps,
          isVideoThumbnail: item.isVideo,
          pictureClassName: `modal-mosaic__mini-tile-picture ${
            selectedItemIndex === index ? 'modal-mosaic__mini-tile-picture--active' : ''
          }`,
        }
      }
    />
  );
};

const VideoPlayerIconMiniTile = (): JSX.Element => {
  return (
    <>
      <div className="modal-mosaic__mini-tile-video modal-mosaic__mini-tile-video--mobile">
        <Icon iconName={'action-play'} color={'white'} size={24} />
      </div>
      <div className="modal-mosaic__mini-tile-video modal-mosaic__mini-tile-video--dsk">
        <Icon iconName={'action-play'} color={'white'} size={24} />
      </div>
    </>
  );
};

const MosaicModal: FC<Partial<TMosaicModalProps>> = (props) => {
  const { items, selectedItem, selectedItemIndex, handleMosaicItemChange, handleClickOnArrow, showOverlayTitleAndDescription } = props;

  const sliderTilesRef = useRef<Slider | null>(null);
  const [slideMove, setSlideMove] = useState<'prev' | 'next' | ''>('');
  const isDesktop = useMediaQuery(`(min-width: ${Breakpoints['dsk']}px)`);

  useEffect(() => {
    const slideCount = isDesktop ? 5 : 3;
    if (selectedItemIndex && selectedItemIndex > slideCount) {
      sliderTilesRef?.current?.slickGoTo(selectedItemIndex);
    }
  }, [isDesktop, items, selectedItemIndex]);

  useEffect(() => {
    if (typeof selectedItemIndex !== 'number') return;
    const nextNum = selectedItemIndex / 2;
    const result = nextNum - Math.floor(nextNum) === 0;
    if (selectedItemIndex && selectedItemIndex > 0 && result && slideMove === 'next') {
      sliderTilesRef?.current?.slickGoTo(selectedItemIndex);
    }
    if (slideMove === 'prev') {
      sliderTilesRef?.current?.slickGoTo(selectedItemIndex);
    }
  }, [selectedItemIndex, slideMove]);

  const moveLeft = (): void => {
    setSlideMove('prev');
    handleClickOnArrow && handleClickOnArrow((selectedItemIndex as number) - 1);
  };

  const moveRight = (): void => {
    setSlideMove('next');
    handleClickOnArrow && handleClickOnArrow((selectedItemIndex as number) + 1);
  };

  const slideClick = (item, index): void => {
    setSlideMove('');
    handleMosaicItemChange?.(item, index);
  };

  const settingsTiles = {
    dots: false,
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    swipeToSlide: true,
    responsive: [
      { breakpoint: Breakpoints['mbl'], settings: { slidesToShow: 4 } },
      { breakpoint: Breakpoints['tbl-p'], settings: { slidesToShow: 5 } },
      { breakpoint: Breakpoints['tbl-l'], settings: { slidesToShow: 4.5 } },
      { breakpoint: Breakpoints['dsk'], settings: { slidesToShow: 6 } },
    ],
  };

  return (
    <div className="modal-mosaic">
      {items && items.length > 0 && (
        <>
          {(selectedItemIndex as number) > 0 && <ArrowLeft moveLeft={moveLeft} />}
          {items && (selectedItemIndex as number) < items?.length - 1 && <ArrowRight moveRight={moveRight} />}
          <div className="modal-mosaic__main-picture-container">
            {items?.map((slide, index, arr) => {
              const editorialImageProps = slide?.editorialImageProps
                ? {
                    desktopImage: slide.editorialImageProps?.desktopImage as TImageField,
                    mobileImage: slide.editorialImageProps?.mobileImage as TImageField,
                    desktopAlt: slide.editorialImageProps.alt,
                  }
                : {};
              return (
                <div
                  key={slide?.id}
                  className={`
                                overlay-carousel-popup__focus-image
                                ${selectedItemIndex && index < selectedItemIndex ? 'overlay-carousel-popup__focus-image--move-left' : ''}
                                `}
                  style={{ zIndex: arr.length - index }}
                >
                  <HeroCard
                    {...slide}
                    {...editorialImageProps}
                    productImageProps={
                      slide.mosaicModalProductImageProps && {
                        ...slide.mosaicModalProductImageProps,
                      }
                    }
                    activeCard={index === selectedItemIndex}
                    key={slide?.id}
                    index={index}
                    showOverlayTitleAndDescription={showOverlayTitleAndDescription}
                    moveLeft={moveLeft}
                    moveRight={moveRight}
                  />
                </div>
              );
            })}
          </div>
          <div className={'modal-mosaic__mini-tile-container'}>
            <Slider ref={sliderTilesRef} {...settingsTiles}>
              {items?.map((item, index) => (
                <div
                  key={item?.title?.value}
                  className={`modal-mosaic__mini-tile ${selectedItemIndex === index ? 'modal-mosaic__mini-tile--active' : ''}`}
                  onClick={(): void => slideClick(item, index)}
                >
                  {item.isVideo && VideoPlayerIconMiniTile()}
                  {MiniTile(index, item, selectedItemIndex)}
                </div>
              ))}
            </Slider>
          </div>
        </>
      )}
      <div className={'modal-mosaic__mini-tile-text-content'}>
        <div className="modal-mosaic__mini-tile-text-content-title">
          {selectedItem?.title && <Text tag={'h3'} field={selectedItem.title} />}
        </div>
        <div className="modal-mosaic__mini-tile-text-content-description">
          {selectedItem?.description && <Text tag={'div'} field={selectedItem.description} />}
        </div>
      </div>
    </div>
  );
};

export default MosaicModal;
